/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {LIVE_HISTORY_LAYER_ID} from '@ion/dls/src/maps/constants';
import {CURRENT_LAYER} from '@ion/dls/src/maps/layers/CurrentLayerSettings';
import {TimeModes, TrackingMode} from '@ion/dls/src/types/types';
import {
  cancelLiveHistory,
  setHistoryActive,
  setLiveHistoryFetching,
  setMapWidgetSelected,
  setTimeMode,
  setTrackingMapLayers,
  setTrackingMode
} from '../../../../store/actions/tracking';
import {
  mapLayerSelector,
  timeModeSelector
} from '../../../../store/selectors/tracking-selectors';

/**
 * The useReturnToLiveTracking hook is used return to the live tracking page.
 * This is the one place every "go back to Live" affordance - the History
 * report's own Back button, the applied filter bar's date/Timeline/History
 * chips, Clear All - converges on, so the map is left the same way
 * regardless of which of those the user actually reached for: the Fleet
 * layer restored, and Timeline/History torn all the way down rather than
 * merely switched away from.
 *
 * ```typescript
 *
 *   import {useReturnToLiveTracking} from '../tracking/hooks/history';
 *
 *   const returnToLiveTracking = useReturnToLiveTracking();
 *
 *   <SomeCTAButton ...
 *    onClick={returnToLiveTracking}
 *    ... />
 *
 * ```
 */
export const useReturnToLiveTracking = () => {
  const dispatch = useDispatch();
  const timeMode = useSelector(timeModeSelector);
  const layers   = useSelector(mapLayerSelector);

  const returnToLiveTracking = useCallback(() => {
    dispatch(setTrackingMode(TrackingMode.Live) as any);

    const currentLayers = layers?.filter(layerId => layerId !== LIVE_HISTORY_LAYER_ID) || [];
    if (!currentLayers.includes(CURRENT_LAYER)) {
      currentLayers.push(CURRENT_LAYER);
    }
    dispatch(setTrackingMapLayers(currentLayers) as any);
    dispatch(cancelLiveHistory() as any);
    dispatch(setMapWidgetSelected(null) as any);
    dispatch(setLiveHistoryFetching(false) as any);

    if (timeMode === TimeModes.timeline || timeMode === TimeModes.history) {
      dispatch(setTimeMode(TimeModes.live) as any);
      dispatch(setHistoryActive(false) as any);
    }
  }, [dispatch, timeMode, layers]);

  return returnToLiveTracking;
};

export default useReturnToLiveTracking;
